import React, {useEffect} from 'react';
import {gql, useLazyQuery} from '@apollo/client';
import {GridColDef} from '@mui/x-data-grid-pro';
import {capitalize, rest} from 'lodash-es';
import {Box, Button} from '@mui/material';
import {Link, useNavigate} from 'react-router-dom';
import {Email} from '@mui/icons-material';
import {
  contactTypeColors,
  propositionTypeColors,
  propositionTypeLabels,
} from '../../components/ContactPage/contactStyles';
import {
  RenderColor,
  RenderPropositionType,
} from '../../components/EditorGrid/GridRenderComponents';
import {ACCOUNT_SEARCH_FRAGMENT} from '../../graphql/fragments';
import {Panel} from '../../components/Panel/Panel';
import {darkBlue, themeColors} from '../../theme/Theme';
import Loading from '../../components/Loading/Loading';
import {LeadsAndAccounts_leadsAndAccounts} from '../../schema-types/LeadsAndAccounts';
import {useGetStaffId} from '../../hooks/useGetStaffId';
import {
  PaginatedAccounts,
  PaginatedAccountsVariables,
} from '../../schema-types/PaginatedAccounts';
import {StickyDataGrid} from '../../components/Common/StickyDataGrid';
import {FeatureBoundary} from '../../contexts/FeatureBoundary';
import {GlobalPermissionEnum} from '../../schema-types/globalTypes';
import AdviserSearch from '../../components/AdviserSearch/AdviserSearch';
import {gridCellDef} from '../../utils/GridCellDef';

export const PaginatedAccountsPage = () => {
  const {currentUserData, staff, setStaff, setShowStaff, showStaff} = useGetStaffId();

  const staffId = staff?.id || currentUserData?.id || null;

  const [fetch, {data, loading: initialLoading}] = useLazyQuery<
    PaginatedAccounts,
    PaginatedAccountsVariables
  >(
    gql`
      query PaginatedAccounts($afterId: String, $staffId: UUID, $before: String) {
        dashboardAccounts(staffId: $staffId, after: $afterId, before: $before) {
          pageInfo {
            hasNextPage
            endCursor
            hasPreviousPage
            startCursor
          }
          totalCount
          edges {
            cursor
            node {
              ...AccountSearchFragment
            }
          }
        }
      }
      ${ACCOUNT_SEARCH_FRAGMENT}
    `,
    {
      variables: {
        staffId: staffId!,
      },
    }
  );
  useEffect(() => {
    if (staffId) {
      fetch({
        variables: {
          staffId,
        },
      });
    }
  }, [staffId]);
  const navigate = useNavigate();

  if (!staff && !staffId) {
    // if no staff and no currently selected user, wait for data
    return <Loading />;
  }

  const items = data?.dashboardAccounts?.edges.map((x) => x!.node!) || [];
  const totalCount = data?.dashboardAccounts?.totalCount || 0;

  return (
    <Panel color={themeColors.blue} title={`${staff?.name} Accounts`}>
      <Box mt={2} width="100%" />
      {showStaff ? (
        <AdviserSearch
          visible={showStaff}
          setVisible={setShowStaff}
          setAdviser={setStaff}
          currentUserData={currentUserData}
        />
      ) : (
        <FeatureBoundary
          features={[GlobalPermissionEnum.can_access_admin_features]}
          showErrorIfDenied={false}
        >
          <Box width="100%" display="flex" justifyContent="flex-end">
            <Button
              variant="outlined"
              onClick={() => setShowStaff(true)}
              data-testid="show-search"
            >
              Show advisor search
            </Button>
          </Box>
        </FeatureBoundary>
      )}
      <Box mt={2} />
      <Box display="flex" minHeight={700}>
        <Box flexGrow={1}>
          <StickyDataGrid
            rowCount={totalCount}
            columns={columns}
            isCellEditable={() => false}
            rows={items.filter((item) => Boolean(item.accountStatus))}
            loading={initialLoading}
            rowHeight={50}
            onRowDoubleClick={(params) => {
              const type = params.row.__typename;
              if (type === 'LeadType') {
                navigate({pathname: `/leads/${params.row.id}/basic`});
              } else {
                navigate({pathname: `/account/${params.row.id}`});
              }
            }}
            {...rest}
          />
        </Box>
      </Box>
    </Panel>
  );
};

type AccountColumnProps = GridColDef & {
  field: string;
};

const RenderAccountNameAndEmail: gridCellDef<LeadsAndAccounts_leadsAndAccounts> = (
  params
) => {
  const account = params.row;
  const email =
    // eslint-disable-next-line no-underscore-dangle
    account.__typename === 'AccountType' ? account.primaryContact?.email : null;
  return (
    <div
      style={{
        flex: 1,
        justifyContent: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 4,
        color: darkBlue,
        textDecoration: 'none',
        lineHeight: 1,
        height: '100%',
      }}
    >
      <Link
        role="a"
        to={{
          pathname:
            // eslint-disable-next-line no-underscore-dangle
            account.__typename === 'LeadType'
              ? `/leads/${params.id}/basic`
              : `/account/${params.id}`,
        }}
        onClick={(e) => {
          e.stopPropagation();
        }}
        style={{fontWeight: 600, color: darkBlue, textDecoration: 'none'}}
      >
        <>{params.row.name || ''} </>
      </Link>
      {params.row.isDeleted ? '(inactive)' : ''}
      {email ? (
        <a
          className="sentry-mask"
          onClick={(e) => e.stopPropagation()} // disable row navigation event
          style={{
            color: darkBlue,
            textAlign: 'center',
            textDecoration: 'none',
            display: 'flex',
            alignItems: 'center',
          }}
          href={`mailto:${email}`}
        >
          <Email style={{marginRight: 4}} fontSize="small" />
          {email}
        </a>
      ) : null}
    </div>
  );
};

export const columns: AccountColumnProps[] = [
  {
    field: 'name',
    headerName: ' ',
    flex: 2,
    renderCell: RenderAccountNameAndEmail,
  },
  {
    field: 'accountStatus',
    headerName: 'Status',
    flex: 1,
    valueGetter: (value, row, column, apiRef) => row.accountStatus || 'Lead',
    renderCell: RenderColor(contactTypeColors),
  },
  {
    field: 'city',
    headerName: 'City',
    valueGetter: (value, row, column, apiRef) =>
      row.city || (row.addresses?.length ? capitalize(row.addresses[0].city) : null),
    flex: 1,
  },
  {
    field: 'adviser.name',
    headerName: 'Adviser',
    flex: 1,
    valueGetter: (value, row, column, apiRef) => row.adviser?.name,
  },
  {
    field: 'primaryContact' as any,
    headerName: 'Primary',
    valueGetter: (value, row, column, apiRef) => row.primaryContact?.name,
    flex: 1,
  },
  {
    field: 'secondaryContact' as any,
    headerName: 'Secondary',
    valueGetter: (value, row, column, apiRef) => row.secondaryContact?.name,
    flex: 1,
  },
  {
    renderCell: RenderPropositionType(propositionTypeColors, propositionTypeLabels),
    valueGetter: (value, row, column, apiRef) => row.propositionType,
    field: 'propositionType',
    headerName: 'Proposition Type',
    flex: 1,
  },
  {
    field: 'primaryPhone',
    headerName: 'Phone',
    valueGetter: (value, row, column, apiRef) =>
      row.primaryPhone || row.homePhone || row.mobilePhone,
    flex: 1,
  },
  {
    field: 'plans',
    headerName: 'Account value',
    flex: 1,
    valueGetter: (value, row, column, apiRef) => `£${row.plans}`,
  },
];
